/**
 * Google OAuth via Firebase Auth — handles sign-in with Calendar, Drive & Gmail scopes.
 * Returns an access_token that can call Google APIs directly from the browser.
 */
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  User,
  OAuthCredential,
} from 'firebase/auth'
import { auth } from './firebase'

// Scopes we request — readonly for all three APIs
const GOOGLE_SCOPES = [
  'https://www.googleapis.com/auth/calendar.readonly',
  'https://www.googleapis.com/auth/drive.readonly',
  'https://www.googleapis.com/auth/gmail.readonly',
  'profile',
  'email',
]
export interface GoogleUser {
  uid: string
  displayName: string | null
  email: string | null
  photoURL: string | null
  accessToken: string
  tokenExpiry: number // timestamp ms
}

// In-memory token cache
let cachedToken: string | null = null
let cachedTokenExpiry: number = 0
function buildProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider()
  GOOGLE_SCOPES.forEach((scope) => provider.addScope(scope))
  provider.setCustomParameters({
    prompt: 'consent',
  })
  return provider
}
function persistGoogleUser(
  user: User,
  accessToken: string,
  tokenExpiry: number,
) {
  cachedToken = accessToken
  cachedTokenExpiry = tokenExpiry
  try {
    localStorage.setItem('vsm_google_token', accessToken)
    localStorage.setItem('vsm_google_token_expiry', tokenExpiry.toString())
    localStorage.setItem(
      'vsm_google_user',
      JSON.stringify({
        uid: user.uid,
        displayName: user.displayName,
        email: user.email,
        photoURL: user.photoURL,
      }),
    )
  } catch {}
}

/**
 * Detect if we're running inside an iframe (e.g. Magic Patterns preview).
 */
function isInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch {
    return true // cross-origin iframe
  }
}

/**
 * Sign in with Google — tries popup first, falls back to redirect if blocked.
 * In iframe environments, attempts redirect flow directly.
 * Returns the Google user info + access_token for API calls.
 */
export async function googleSignIn(): Promise<GoogleUser> {
  const provider = buildProvider()
  const inIframe = isInIframe()

  // In iframe context, popup will always be blocked — try redirect directly
  if (inIframe) {
    console.warn(
      '[GoogleAuth] Running inside iframe — popup auth is not available.',
    )
    // Attempt redirect (may not work in sandboxed iframes either)
    try {
      localStorage.setItem('vsm_google_auth_pending', 'redirect')
    } catch {}
    try {
      await signInWithRedirect(auth, provider)
    } catch (redirectErr: any) {
      console.error('[GoogleAuth] Redirect also failed in iframe:', redirectErr)
      throw new Error(
        'La autenticación con Google no está disponible en este entorno. Abrí la app en una pestaña nueva para conectar tu cuenta de Google.',
      )
    }
    throw new Error('Redirigiendo a Google para autenticación...')
  }

  // Normal flow: try popup first
  try {
    const result = await signInWithPopup(auth, provider)
    const credential = GoogleAuthProvider.credentialFromResult(result)
    if (!credential?.accessToken) {
      throw new Error('No se pudo obtener el access token de Google')
    }
    const user = result.user
    const accessToken = credential.accessToken
    const tokenExpiry = Date.now() + 3500 * 1000
    persistGoogleUser(user, accessToken, tokenExpiry)
    console.log('[GoogleAuth] Signed in via popup as', user.email)
    return {
      uid: user.uid,
      displayName: user.displayName,
      email: user.email,
      photoURL: user.photoURL,
      accessToken,
      tokenExpiry,
    }
  } catch (popupError: any) {
    // User cancelled — not an error
    if (
      popupError?.code === 'auth/popup-closed-by-user' ||
      popupError?.code === 'auth/cancelled-popup-request'
    ) {
      throw popupError
    }

    // Popup blocked or unauthorized domain — fall back to redirect
    const isPopupBlocked =
      popupError?.code === 'auth/popup-blocked' ||
      popupError?.code === 'auth/unauthorized-domain'
    if (isPopupBlocked) {
      console.warn('[GoogleAuth] Popup blocked, falling back to redirect')
      try {
        localStorage.setItem('vsm_google_auth_pending', 'redirect')
      } catch {}
      await signInWithRedirect(auth, provider)
      throw new Error('Redirigiendo a Google para autenticación...')
    }

    // Re-throw other errors
    throw popupError
  }
}

/**
 * Check for pending redirect result (call on app startup).
 * Returns GoogleUser if a redirect sign-in just completed, null otherwise.
 */
export async function checkRedirectResult(): Promise<GoogleUser | null> {
  try {
    const pending = localStorage.getItem('vsm_google_auth_pending')
    if (pending !== 'redirect') return null
    localStorage.removeItem('vsm_google_auth_pending')
    const result = await getRedirectResult(auth)
    if (!result) return null
    const credential = GoogleAuthProvider.credentialFromResult(result)
    if (!credential?.accessToken) return null
    const user = result.user
    const accessToken = credential.accessToken
    const tokenExpiry = Date.now() + 3500 * 1000
    persistGoogleUser(user, accessToken, tokenExpiry)
    console.log('[GoogleAuth] Signed in via redirect as', user.email)
    return {
      uid: user.uid,
      displayName: user.displayName,
      email: user.email,
      photoURL: user.photoURL,
      accessToken,
      tokenExpiry,
    }
  } catch (err) {
    console.error('[GoogleAuth] Redirect result error:', err)
    localStorage.removeItem('vsm_google_auth_pending')
    return null
  }
}

/**
 * Sign out of Google (Firebase auth).
 */
export async function googleSignOut(): Promise<void> {
  await signOut(auth)
  cachedToken = null
  cachedTokenExpiry = 0
  try {
    localStorage.removeItem('vsm_google_token')
    localStorage.removeItem('vsm_google_token_expiry')
    localStorage.removeItem('vsm_google_user')
  } catch {}
  console.log('[GoogleAuth] Signed out')
}

/**
 * Get the current Google access token.
 * Returns null if not signed in or token expired.
 */
export function getGoogleToken(): string | null {
  // Check memory cache first
  if (cachedToken && Date.now() < cachedTokenExpiry) {
    return cachedToken
  }

  // Check localStorage
  try {
    const token = localStorage.getItem('vsm_google_token')
    const expiry = localStorage.getItem('vsm_google_token_expiry')
    if (token && expiry && Date.now() < parseInt(expiry)) {
      cachedToken = token
      cachedTokenExpiry = parseInt(expiry)
      return token
    }
  } catch {}
  return null
}

/**
 * Check if user is connected to Google.
 */
export function isGoogleConnected(): boolean {
  return getGoogleToken() !== null
}

/**
 * Get stored Google user info (without requiring auth state listener).
 */
export function getStoredGoogleUser(): Omit<
  GoogleUser,
  'accessToken' | 'tokenExpiry'
> | null {
  try {
    const raw = localStorage.getItem('vsm_google_user')
    if (raw) return JSON.parse(raw)
  } catch {}
  return null
}

/**
 * Subscribe to Firebase auth state changes.
 */
export function onGoogleAuthChange(
  callback: (user: User | null) => void,
): () => void {
  return onAuthStateChanged(auth, callback)
}

/**
 * Re-authenticate silently to refresh the token.
 * If the user's Firebase session is still valid, this gets a fresh token.
 */
export async function refreshGoogleToken(): Promise<string | null> {
  const user = auth.currentUser
  if (!user) return null
  try {
    // Force token refresh
    const idToken = await user.getIdToken(true)
    // Unfortunately, Firebase doesn't give us a new Google access_token
    // via getIdToken. The user needs to re-sign-in for a new access_token.
    // We return null to signal that re-auth is needed.
    return null
  } catch {
    return null
  }
}
